/**
 * MemoryManager — Modal zum Anzeigen/Löschen von Cross-Session-Memory (#1180).
 */
import { useEffect, useState } from 'react';
import { X, Brain, Trash2 } from 'lucide-react';
import { useLanguage } from '../../context/LanguageContext';

const API = '';

export default function MemoryManager({ open, onClose }) {
  const { t } = useLanguage();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    setLoading(true);
    fetch(`${API}/api/chat/memory`, { credentials: 'include' })
      .then(r => r.ok ? r.json() : [])
      .then(d => setRows(Array.isArray(d) ? d : []))
      .catch(() => setRows([]))
      .finally(() => setLoading(false));
  }, [open]);

  const remove = async (id) => {
    if (!confirm(t('chat.memory_delete') || 'Memory-Eintrag löschen?')) return;
    const r = await fetch(`${API}/api/chat/memory/${id}`, { method: 'DELETE', credentials: 'include' });
    if (r.ok) setRows(rows.filter(x => x.id !== id));
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-surface-card rounded-[14px] w-full max-w-2xl max-h-[80vh] flex flex-col shadow-elevated overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-line">
          <div className="flex items-center gap-2">
            <Brain size={18} className="text-[#338edc]" />
            <h3 className="text-base font-semibold text-[var(--text-primary)]">
              {t('chat.memory_title') || 'Cross-Session-Memory'}
            </h3>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-[8px] hover:bg-surface-hover text-[var(--text-tertiary)]">
            <X size={16} />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading && <div className="text-sm text-[var(--text-tertiary)] text-center py-8">…</div>}
          {!loading && rows.length === 0 && (
            <div className="text-sm text-[var(--text-tertiary)] text-center py-8">
              {t('chat.memory_empty') || 'Noch keine gespeicherten Erinnerungen.'}
            </div>
          )}
          {rows.map((r) => (
            <div key={r.id} className="border border-line rounded-[10px] p-3 mb-2 flex items-start gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide bg-surface-input text-[var(--text-secondary)] font-medium">
                    {r.type || 'memory'}
                  </span>
                  {r.created_at && (
                    <span className="text-xs text-[var(--text-tertiary)]">
                      {new Date(r.created_at).toLocaleDateString()}
                    </span>
                  )}
                </div>
                <div className="text-sm text-[var(--text-primary)] whitespace-pre-wrap break-words">{r.content}</div>
              </div>
              <button
                onClick={() => remove(r.id)}
                className="p-1.5 rounded-[8px] hover:bg-red-50 dark:hover:bg-red-500/10 text-[var(--text-tertiary)] hover:text-red-500"
                title={t('chat.delete') || 'Löschen'}
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
